import { useMemo, useState } from "react";
import { ChoiceField, NumField } from "@/components/tool/CalcFields";
import { rcfFromRpm, rpmFromRcf } from "@/lib/science/gforce";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";

type Mode = "rpmToRcf" | "rcfToRpm";

export function GForceCalc() {
  const locale = getLocale();
  const [mode, setMode] = useState<Mode>("rpmToRcf");
  const [radius, setRadius] = useState("10");
  const [rpm, setRpm] = useState("10000");
  const [rcf, setRcf] = useState("11180");

  const out = useMemo(() => {
    const r = Number(radius);
    if (mode === "rpmToRcf") {
      const value = rcfFromRpm(r, Number(rpm));
      return value == null
        ? null
        : { label: m.gf_rcf_label(), value, unit: "× g" };
    }
    const value = rpmFromRcf(r, Number(rcf));
    return value == null
      ? null
      : { label: m.gf_rpm_label(), value, unit: "RPM" };
  }, [mode, radius, rpm, rcf]);

  const num = (n: number) =>
    n.toLocaleString(locale, { maximumFractionDigits: 0 });

  return (
    <div className="sci-card rounded-2xl space-y-4 p-6 sm:p-8">
      <ChoiceField
        label={m.gf_mode()}
        value={mode}
        onChange={setMode}
        options={[
          { value: "rpmToRcf", label: m.gf_mode_rpm_to_rcf() },
          { value: "rcfToRpm", label: m.gf_mode_rcf_to_rpm() },
        ]}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <NumField
          id="radius"
          label={m.gf_radius()}
          value={radius}
          onChange={setRadius}
          suffix="cm"
        />
        {mode === "rpmToRcf" ? (
          <NumField
            id="rpm"
            label={m.gf_rpm()}
            value={rpm}
            onChange={setRpm}
            suffix="RPM"
          />
        ) : (
          <NumField
            id="rcf"
            label={m.gf_rcf()}
            value={rcf}
            onChange={setRcf}
            suffix="× g"
          />
        )}
      </div>

      {out ? (
        <div className="sci-result-zone flex items-baseline justify-between gap-4 rounded-xl px-5 py-4">
          <span className="text-sm font-medium text-muted-foreground">
            {out.label}
          </span>
          <span
            data-testid="gf-result"
            className="sci-result font-display text-3xl font-bold sm:text-4xl"
          >
            {num(out.value)} {out.unit}
          </span>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{m.gf_hint()}</p>
      )}
    </div>
  );
}
